import moment from "moment";
import {
  getCabinClass,
  getFareType,
  getNewCabinClass,
  getNewTripType,
  getTravelType,
  getTripType,
} from "./flightUtils";

export function formatIndianNumber(number: number): string {
  if (number === null || number === undefined || isNaN(number)) return "";
  const [integerPart, decimalPart] = number.toFixed(2).split(".");
  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  const formattedOtherNumbers = otherNumbers.replace(
    /\B(?=(\d{2})+(?!\d))/g,
    ","
  );
  const formattedNumber =
    (formattedOtherNumbers ? formattedOtherNumbers + "," : "") + lastThree;
  return `${formattedNumber}.${decimalPart}`;
}

export function capitalizeFirstLetter(string: string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const parseQueryParams = (search: string) => {
  const queryParams: any = new URLSearchParams(search);
  const params: any = {};
  for (const [key, value] of queryParams.entries()) {
    params[key] = value;
  }
  return params;
};

export const generatePayloadFromUrl = (url: any) => {
  const queryParams = parseQueryParams(url);

  const itinerary = queryParams.itinerary.split("_");
  const routes = itinerary.map((route: any) => {
    const [from, to, date] = route.split("-");
    return {
      fromCityOrAirport: { code: from },
      toCityOrAirport: { code: to },
      travelDate: moment(date, "DD/MM/YYYY").format("YYYY-MM-DD"),
    };
  });

  const [adultCount, childCount, infantCount] = queryParams.paxType
    .split("_")
    .map((pax: any) => parseInt(pax.split("-")[1], 10));

  const payload = {
    searchQuery: {
      cabinClass: getCabinClass(queryParams.cabinClass)?.toUpperCase(),
      paxInfo: {
        ADULT: `${adultCount}`,
        CHILD: `${childCount}`,
        INFANT: `${infantCount}`,
      },
      routeInfos: routes,
      searchModifiers: {
        pft:
          getFareType(queryParams.pft) === "Regular"
            ? ""
            : getFareType(queryParams.pft)?.toUpperCase()?.replace(" ", "_"),
        isDirectFlight: false,
        isConnectingFlight: false,
      },
    },
  };
  return payload;
};

export const generateNewPayloadFromUrl = (url: any) => {
  const queryParams = parseQueryParams(url);
  const itinerary = queryParams?.itinerary?.split("_");
  const pft = queryParams?.pft;
  const tripInfo = itinerary?.map((route: any, index: number) => {
    const [from, to, date] = route.split("-");
    return {
      Origin: from,
      Destination: to,
      TravelDate: moment(date, "DD/MM/YYYY").format("MM/DD/YYYY"),
      Trip_Id: index,
    };
  });

  const [adultCount, childCount, infantCount] = queryParams?.paxType
    .split("_")
    .map((pax: any) => parseInt(pax.split("-")[1], 10));

  const isInternational = itinerary?.some((route: any) => {
    const [from, to] = route.split("-");
    return getTravelType(from, to) === 1;
  });

  const travelType = isInternational ? 1 : 0;

  const payload = {
    Travel_Type: travelType,
    Booking_Type: getNewTripType(queryParams?.triptype) || 0,
    // Booking_Type: 2,
    TripInfo: tripInfo,
    Adult_Count: `${adultCount}`,
    Child_Count: `${childCount}`,
    Infant_Count: `${infantCount}`,
    Class_Of_Travel: `${getNewCabinClass(queryParams?.cabinClass) || 0}`,
    SrCitizen_Search: pft === "SC",
    StudentFare_Search: pft === "STU",
    DefenceFare_Search: pft === "DF",
    InventoryType: 0,
    Filtered_Airline: [
      {
        Airline_Code: queryParams?.filteredAirline || "",
      },
    ],
  };

  return payload;
};

export const extractPaxFromUrl = (url: any) => {
  const queryParams = parseQueryParams(url);

  const shortTripType = queryParams?.triptype;

  const [adultCount, childCount, infantCount] = queryParams.paxType
    .split("_")
    .map((pax: any) => parseInt(pax.split("-")[1], 10));

  const itinerary = queryParams.itinerary.split("_");

  const firstSegment = itinerary[0];
  const fromLocation = firstSegment.split("-")[0];

  const lastSegment = itinerary[itinerary.length - 1];
  const toLocation = lastSegment.split("-")[1];
  const tripType =
    shortTripType === "R"
      ? "ROUNDTRIP"
      : shortTripType === "S"
      ? "SPECIALROUNDTRIP"
      : shortTripType === "M"
      ? "MULTICITY"
      : "ONEWAY";

  return {
    ADULT: adultCount,
    CHILD: childCount,
    INFANT: infantCount,
    tripType,
    fromLocation,
    toLocation,
  };
};

export const generateObjectFromUrl = (
  search: string,
  flightSearchDetails: any
) => {
  const queryParams = parseQueryParams(search);

  let multicities: any[] = [];

  const [adultCount, childCount, infantCount] = queryParams.paxType
    .split("_")
    .map((pax: any) => parseInt(pax.split("-")[1], 10));

  if (queryParams.triptype === "M") {
    // Handle Multicity trip type
    const itinerarySegments = queryParams?.itinerary?.split("_");

    multicities = itinerarySegments.map((segment: string) => {
      const [from, to, departureDate] = segment.split("-");
      return {
        from: from || "AMD",
        to: to || "BOM",
        departureDate:
          moment(departureDate, "DD/MM/YYYY").toISOString() ||
          moment().toISOString(),
      };
    });

    return {
      triptype: getTripType(queryParams.triptype) || "One Way",
      ...(multicities.length > 0 && { multicities }),
      from: flightSearchDetails?.from || "AMD",
      to: flightSearchDetails?.to || "BOM",
      departureDate:
        moment(
          flightSearchDetails?.departureDate,
          "DD/MM/YYYY"
        ).toISOString() || moment().toISOString(),
      returnDate: flightSearchDetails?.returnDate
        ? moment(flightSearchDetails?.returnDate, "DD/MM/YYYY").toISOString()
        : moment(
            flightSearchDetails?.departureDate,
            "DD/MM/YYYY"
          ).toISOString(),
      ADULT: adultCount || 1,
      CHILD: childCount || 0,
      INFANT: infantCount || 0,
      cabinClass: getCabinClass(queryParams.cabinClass) || "Economy",
      specialfare: getFareType(queryParams.pft) || "Regular",
    };
  } else {
    const itinerary = queryParams?.itinerary?.split("_")[0];
    const [from, to, departureDate] = itinerary?.split("-");

    const returnSegment =
      queryParams.triptype === "R" || queryParams.triptype === "S"
        ? queryParams.itinerary.split("_")[1]
        : null;
    const returnDate = returnSegment ? returnSegment.split("-")[2] : null;

    return {
      from: from || "AMD",
      triptype: getTripType(queryParams.triptype) || "One Way",
      to: to || "BOM",
      multicities: flightSearchDetails?.multicities,
      departureDate:
        moment(departureDate, "DD/MM/YYYY").toISOString() ||
        moment().toISOString(),
      returnDate: returnDate
        ? moment(returnDate, "DD/MM/YYYY").toISOString()
        : moment(departureDate, "DD/MM/YYYY").toISOString(),
      ADULT: adultCount || 1,
      CHILD: childCount || 0,
      INFANT: infantCount || 0,
      cabinClass: getCabinClass(queryParams.cabinClass) || "Economy",
      specialfare: getFareType(queryParams.pft) || "Regular",
    };
  }
};

export const handleUppercaseChange = (
  formik: any,
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const { name, value } = e.target;
  formik.setFieldValue(name, value?.toUpperCase());
};

export const maskName = (name: string) => {
  const parts = name?.trim()?.split(" ");
  return parts
    ?.map((part, index) => {
      if (index === 0 || index === 1) {
        return part.length <= 3
          ? part
          : part.slice(0, 3) + "*".repeat(part.length - 3);
      } else {
        return part.length <= 3 ? part : part.slice(0, 3) + "...";
      }
    })
    ?.join(" ");
};
